import { useEffect, useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { BordButton } from 'bord-design-system';
import { EQuoteItemStatusCode, IQuoteItem, IQuoteSummary } from 'types/quote.types';
import { QUOTE_STATUS_CODE } from 'types/quoteDetails.types';
import { QuoteEditorService } from 'services/quote-editor.service';
import QuoteConfirmationModal from 'views/Quote/QuoteEditor/components/QuoteConfirmationModal/QuoteConfirmationModal';
import { displayErrorNotification } from 'utils/displayNudosStandardNotifications';
import { IQuoteItemDetailsForQuoteManagement } from 'types/quoteEditor.types';
import { formatQuotePrice } from 'views/Quote/utils/formatQuotePrice';

import './QuoteDetailApprovalCounter.scss';

interface IQuoteDetailApprovalCounter {
  summary?: IQuoteSummary;
  quoteId?: number;
  clientName?: string;
  countryName?: string;
  countryCode?: string;
  organizationId?: number;
  onSendSuccess?: () => void;
  quoteStatusCode?: QUOTE_STATUS_CODE;
  items: IQuoteItem[];
  parentQuoteId?: number;
}

const QUOTE_STATUS_CODE_QUOTING = [QUOTE_STATUS_CODE.PENDING, QUOTE_STATUS_CODE.TO_QUOTE];

/**
 * Contador de aprobación de herramientas cotizadas con barra de progreso y CTA.
 * Permite iniciar, continuar o enviar la cotización según su estado de avance.
 */
const QuoteDetailApprovalCounter = ({
  summary,
  quoteId,
  clientName,
  countryName,
  countryCode,
  organizationId,
  onSendSuccess,
  quoteStatusCode,
  items
}: IQuoteDetailApprovalCounter) => {
  const { push } = useHistory();
  const { id } = useParams<{ id: string }>();
  const [displayWidth, setDisplayWidth] = useState(0);
  const [displayCount, setDisplayCount] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [quoteItems, setQuoteItems] = useState<IQuoteItemDetailsForQuoteManagement[] | null>(null);
  const toolsStatus = Object.fromEntries(items.map(item => [item.quoteItemId, item?.quoteStatus?.code]));

  const rafRef = useRef<number | null>(null);
  const isQuoting = QUOTE_STATUS_CODE_QUOTING.includes(quoteStatusCode as QUOTE_STATUS_CODE);
  const isCancelled = quoteStatusCode === QUOTE_STATUS_CODE.CANCELLED;
  const showSendBtn = !!summary?.quotedItemsQuantity && summary?.quotedItemsQuantity > 0 && isQuoting;
  const isSendBtnDisabled =
    !summary?.quotedItemsQuantity || !summary?.itemsQuantity || summary.quotedItemsQuantity < summary.itemsQuantity;

  const progressPercentage = summary?.itemsQuantity
    ? Math.min(((summary.quotedItemsQuantity ?? 0) / summary.itemsQuantity) * 100, 100)
    : 0;

  const quotedCount = quoteItems?.filter(
    item => toolsStatus[item.quoteItemId] !== EQuoteItemStatusCode.PENDING_QUOTE
  ).length;

  /** Redirige al editor de cotización con el id de la URL actual. */
  const handleStartQuote = () => {
    if (id) push(`/quote/edit/${id}`);
  };

  /** Formatea el monto total cotizado usando el total del summary y su moneda de venta. */
  const computeTotalAmount = (): string => {
    const total = summary?.totalQuoted;
    if (total == null) return '-';
    return formatQuotePrice(total, summary?.saleCurrency ?? 'USD');
  };

  /** Envía la cotización al cliente con la fecha de vencimiento seleccionada y refresca la data. */
  const handleConfirmSend = async (expirationDate: string) => {
    if (!quoteId) return;
    setIsSending(true);
    try {
      await QuoteEditorService.sendQuoteFromProcurement(quoteId, { expirationDate: new Date(expirationDate) });
      setShowModal(false);
      onSendSuccess?.();
    } catch {
      displayErrorNotification();
    } finally {
      setIsSending(false);
    }
  };

  const animateCount = (target: number) => {
    const duration = 800;
    const startTime = performance.now();

    const step = (currentTime: number) => {
      const progress = Math.min((currentTime - startTime) / duration, 1);
      setDisplayCount(Math.floor(progress * target));
      if (progress < 1) rafRef.current = requestAnimationFrame(step);
    };

    rafRef.current = requestAnimationFrame(step);
  };

  /** Obtiene los ítems del editor para calcular la cantidad cotizada en el modal de envío. */
  const fetchEditorData = async () => {
    if (!quoteId) return;
    const editorItems = await QuoteEditorService.getQuoteEditorItems(quoteId);
    setQuoteItems(editorItems);
  };

  const getStartBtnLabel = () => {
    if (!isSendBtnDisabled) return { label: 'Editar cotización', showOutlinedButton: true };
    if (showSendBtn) return { label: 'Continuar cotización', showOutlinedButton: true };
    return { label: 'Empezar cotización', showOutlinedButton: false };
  };

  useEffect(() => {
    fetchEditorData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quoteId]);

  useEffect(() => {
    setDisplayWidth(progressPercentage);
  }, [progressPercentage]);

  useEffect(() => {
    const target = summary?.quotedItemsQuantity ?? 0;
    if (!target) return;
    animateCount(target);
    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [summary?.quotedItemsQuantity]);

  return (
    <div className="quoteDetailApprovalCounter">
      {showModal && quoteId && (
        <QuoteConfirmationModal
          quoteId={quoteId}
          clientName={clientName ?? ''}
          countryName={countryName ?? ''}
          countryCode={countryCode ?? ''}
          quotedToolsCount={quotedCount ?? 0}
          totalAmount={computeTotalAmount()}
          organizationId={organizationId}
          onClose={() => setShowModal(false)}
          onConfirm={handleConfirmSend}
        />
      )}
      <div className="counterCard">
        <div className="counterRow">
          <span className="counterLabel">Herramientas cotizadas</span>
          <span className="counterValue">
            {displayCount} de {summary?.itemsQuantity ?? 0}
          </span>
        </div>
        <div className="progressBar">
          <div className="progressFill" style={{ width: `${displayWidth}%` }} />
        </div>
      </div>
      <div className="startBtnContainer">
        {showSendBtn && (
          <BordButton
            label="Enviar cotización"
            buttonIconOne="send"
            customClassName="sendBtn"
            onClick={() => setShowModal(true)}
            disabled={isSendBtnDisabled || isSending || isCancelled}
            isLoading={isSending}
          />
        )}
        <BordButton
          label={getStartBtnLabel().label}
          buttonIconOne="edit"
          customClassName="startBtn"
          onClick={handleStartQuote}
          style={{ height: '4.4rem' }}
          bordButtonStyle={getStartBtnLabel()?.showOutlinedButton ? 'outlined' : 'filled'}
          disabled={isSending || isCancelled || !isQuoting}
        />
      </div>
    </div>
  );
};

export default QuoteDetailApprovalCounter;
